const COLOR_LINK_SELECTOR = '[data-color-sibling-link]';
const LEGACY_COLOR_INPUT_SELECTOR = 'input[data-option-group="color_siblings"][data-connected-product-url]';
const INTERNAL_PARAMS = ['variant', 'view', 'selling_plan', '_pos', '_sid', '_ss', '_fid'];

function parsesTrue(value) {
  if (typeof value !== 'string') return false;

  try {
    return JSON.parse(value) === true;
  } catch {
    return false;
  }
}

export function isColorGroupDiscoveryEnabled(doc = globalThis.document) {
  return parsesTrue(doc?.documentElement?.dataset?.colorGroupDiscoveryEnabled);
}

function getConnectionSaveData(connection) {
  return Boolean(connection && connection.saveData);
}

function getIdleScheduler(requestIdleCallback) {
  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback;
  }

  return (callback) => {
    callback();
    return 0;
  };
}

function getIdleCanceller(cancelIdleCallback) {
  if (typeof cancelIdleCallback === 'function') {
    return cancelIdleCallback;
  }

  return () => {};
}

export function normalizeSiblingUrl(href, origin = window.location.origin) {
  const url = new URL(href, origin);

  for (const key of INTERNAL_PARAMS) {
    url.searchParams.delete(key);
  }

  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('utm_') || key.startsWith('_')) {
      url.searchParams.delete(key);
    }
  }

  return url;
}

export function resolveSiblingHref(target, origin = window.location.origin) {
  const href = target?.href || target?.dataset?.connectedProductUrl || target?.dataset?.colorSiblingLink;
  if (!href) return '';

  return normalizeSiblingUrl(href, origin).toString();
}

export function isPlainNavigationEvent(event) {
  return (
    !event.defaultPrevented &&
    (event.button === undefined || event.button === 0) &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function navigateToSiblingHref(href, win = globalThis.window) {
  if (!href) return;
  win?.location.assign(href);
}

export class ColorGroupPrefetchManager {
  #active = new Map();
  #cache = new Map();
  #cancelIdleCallback;
  #connection;
  #fetch;
  #maxEntries;
  #origin;
  #requestIdleCallback;

  constructor(options = {}) {
    this.#origin = new URL(options.origin ?? globalThis.window?.location?.origin ?? 'https://example.test').origin;
    this.#maxEntries = Math.max(0, options.maxEntries ?? 6);
    this.#connection = options.connection ?? globalThis.navigator?.connection;
    this.#fetch = options.fetch ?? globalThis.fetch?.bind(globalThis);
    this.#requestIdleCallback = getIdleScheduler(options.requestIdleCallback ?? globalThis.requestIdleCallback);
    this.#cancelIdleCallback = getIdleCanceller(options.cancelIdleCallback ?? globalThis.cancelIdleCallback);
  }

  get cachedUrls() {
    return [...this.#cache.keys()];
  }

  get activeUrls() {
    return [...this.#active.keys()];
  }

  schedule(href) {
    if (!href || getConnectionSaveData(this.#connection) || typeof this.#fetch !== 'function') return null;

    const url = normalizeSiblingUrl(href, this.#origin);
    const cacheKey = url.toString();
    if (url.origin !== this.#origin || this.#cache.has(cacheKey)) return null;

    const activeRequest = this.#active.get(cacheKey);
    if (activeRequest) return activeRequest.controller;

    const controller = new AbortController();
    const request = { controller, idleId: undefined };
    this.#active.set(cacheKey, request);

    request.idleId = this.#requestIdleCallback(() => {
      if (controller.signal.aborted || this.#active.get(cacheKey) !== request) {
        if (this.#active.get(cacheKey) === request) this.#active.delete(cacheKey);
        return;
      }

      Promise.resolve()
        .then(() =>
          this.#fetch(cacheKey, {
            cache: 'force-cache',
            credentials: 'same-origin',
            priority: 'low',
            signal: controller.signal,
          })
        )
        .then((response) => {
          if (controller.signal.aborted || this.#active.get(cacheKey) !== request || response?.ok === false) return;

          this.#cache.set(cacheKey, true);
          while (this.#cache.size > this.#maxEntries) {
            const oldest = this.#cache.keys().next().value;
            this.#cache.delete(oldest);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (this.#active.get(cacheKey) === request) this.#active.delete(cacheKey);
        });
    });

    return controller;
  }

  cancel(href) {
    const url = normalizeSiblingUrl(href, this.#origin).toString();
    const active = this.#active.get(url);
    if (!active) return;

    this.#cancelIdleCallback(active.idleId);
    active.controller.abort();
    this.#active.delete(url);
  }

  dispose() {
    for (const active of this.#active.values()) {
      this.#cancelIdleCallback(active.idleId);
      active.controller.abort();
    }

    this.#active.clear();
  }
}

function findSiblingTarget(eventTarget) {
  if (!(eventTarget instanceof Element)) return null;
  return eventTarget.closest(`${COLOR_LINK_SELECTOR}, ${LEGACY_COLOR_INPUT_SELECTOR}`);
}

function handleLegacyInputNavigation(event, win) {
  const target = findSiblingTarget(event.target);
  if (!(target instanceof HTMLInputElement)) return;

  const href = resolveSiblingHref(target, win.location.origin);
  if (!href) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  navigateToSiblingHref(href, win);
}

function injectTransitionStyles(doc = globalThis.document) {
  if (!doc?.head || typeof doc.querySelector !== 'function' || typeof doc.createElement !== 'function') return;
  if (doc.querySelector('link[data-color-group-transitions]')) return;

  const scriptUrl = new URL(import.meta.url);
  const link = doc.createElement('link');
  link.rel = 'stylesheet';
  link.href = scriptUrl.href.replace(/color-group-navigation\.js(?:\?.*)?$/, 'color-group-transitions.css');
  link.dataset.colorGroupTransitions = 'true';
  doc.head.appendChild(link);
}

export function initColorGroupNavigation(doc = globalThis.document, win = globalThis.window) {
  if (!doc || !win || !isColorGroupDiscoveryEnabled(doc)) return () => {};

  const listenerController = new AbortController();
  const { signal } = listenerController;
  const prefetchManager = new ColorGroupPrefetchManager({
    origin: win.location.origin,
    connection: win.navigator?.connection,
    fetch: typeof win.fetch === 'function' ? win.fetch.bind(win) : undefined,
    requestIdleCallback:
      typeof win.requestIdleCallback === 'function' ? win.requestIdleCallback.bind(win) : undefined,
    cancelIdleCallback: typeof win.cancelIdleCallback === 'function' ? win.cancelIdleCallback.bind(win) : undefined,
  });
  injectTransitionStyles(doc);

  doc.addEventListener('pointerover', (event) => {
    const target = findSiblingTarget(event.target);
    const href = target ? resolveSiblingHref(target, win.location.origin) : '';
    if (href) prefetchManager.schedule(href);
  }, { signal });

  doc.addEventListener('focusin', (event) => {
    const target = findSiblingTarget(event.target);
    const href = target ? resolveSiblingHref(target, win.location.origin) : '';
    if (href) prefetchManager.schedule(href);
  }, { signal });

  doc.addEventListener('pointerout', (event) => {
    const target = findSiblingTarget(event.target);
    const href = target ? resolveSiblingHref(target, win.location.origin) : '';
    if (href) prefetchManager.cancel(href);
  }, { signal });

  doc.addEventListener('focusout', (event) => {
    const target = findSiblingTarget(event.target);
    const href = target ? resolveSiblingHref(target, win.location.origin) : '';
    if (href) prefetchManager.cancel(href);
  }, { signal });

  doc.addEventListener('click', (event) => {
    const target = findSiblingTarget(event.target);
    if (!(target instanceof HTMLInputElement)) return;
    if (!isPlainNavigationEvent(event)) return;
    handleLegacyInputNavigation(event, win);
  }, { capture: true, signal });

  doc.addEventListener('change', (event) => handleLegacyInputNavigation(event, win), { capture: true, signal });

  return () => {
    listenerController.abort();
    prefetchManager.dispose();
  };
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  initColorGroupNavigation(document, window);
}
